import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { HttpService } from 'src/app/http/http.service';
import { PaymentIframeModalComponent } from 'src/app/shared/components/payment-iframe-modal/payment-iframe-modal.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import {
  PaymentOrderType,
  buildPaymentResultUrl,
  formatCountdown,
  getPaymentRemainingMs,
  storePaymentSessionSnapshot
} from 'src/app/shared/utils/payment-flow.util';
import { ConstantUtil } from 'src/app/util/constant.util';

type BalanceType = 'uupon';

interface RetentionPurchaseOption {
  ruleId?: string | null;
  name?: string;
  description?: string;
  retentionDays?: number | null;
  effectValue?: string | null;
  effectUnit?: string | null;
  extraDays?: number | null;
  price?: number | null;
  actionType?: string | null;
  estimatedUpgradeCreditCny?: number | null;
  estimatedPayableCash?: number | null;
  estimatedRefundAilePoints?: number | null;
  upgradeCreditCny?: number | null;
  payableCash?: number | null;
  priceHint?: string | null;
  unitPrice?: string | null;
  currency?: string | null;
  recommend?: boolean | null;
  sort?: number | null;
}

type RetentionEntryFocus = 'unpaid' | 'queued' | null;
type RetentionOverviewTab = 'purchase' | 'unpaid' | 'queued';

interface RetentionPurchaseModalData {
  quotaCode?: string | null;
  quotaName?: string | null;
  tenantId?: string | null;
  planId?: string | null;
  planName?: string | null;
  aipoolTenantId?: string | null;
  currentValue?: string | null;
  currentDays?: number | null;
  defaultDays?: number | null;
  freeDays?: number | null;
  enabled?: boolean | null;
  originalPriceCny?: number | null;
  upgradeCreditCny?: number | null;
  upgradeCreditPoints?: number | null;
  options?: RetentionPurchaseOption[] | null;
  entryFocus?: RetentionEntryFocus;
}

interface RetentionPrivilegeSnapshot {
  id?: string | null;
  retentionDays?: number | null;
  status?: string | null;
  orderId?: string | null;
  pricingCode?: string | null;
  pricingItemId?: string | null;
  priceCny?: number | null;
  startTime?: number | null;
  activateTime?: number | null;
  expireTime?: number | null;
  paidTime?: number | null;
  graceEndTime?: number | null;
  originalPriceCny?: number | null;
  upgradeCreditCny?: number | null;
  upgradeCreditPoints?: number | null;
  compensationAilePoints?: number | null;
}

interface RetentionUnpaidOrderSnapshot {
  id?: string | null;
  orderId?: string | null;
  retentionDays?: number | null;
  targetRetentionDays?: number | null;
  status?: string | null;
  pricingCode?: string | null;
  pricingItemId?: string | null;
  priceCny?: number | null;
  originalPriceCny?: number | null;
  upgradeCreditCny?: number | null;
  payableCash?: number | null;
  compensationAilePoints?: number | null;
  createTime?: number | null;
  payStartTime?: number | null;
  payExpireTime?: number | null;
  reserveTimeoutSeconds?: number | null;
  paymentUrl?: string | null;
  needPay?: boolean | null;
  latestOrderStatus?: string | null;
  paymentStatus?: string | null;
  actionType?: string | null;
}

interface RetentionPrivilegePayload {
  quotaCode?: string | null;
  active?: RetentionPrivilegeSnapshot | null;
  pending?: RetentionPrivilegeSnapshot | null;
  queuedAddon?: RetentionPrivilegeSnapshot | null;
  unpaidOrder?: RetentionUnpaidOrderSnapshot | null;
}

interface RetentionOptionViewModel {
  key: string;
  title: string;
  description: string;
  priceText: string;
  extraText: string;
  recommend: boolean;
  sort: number;
  price: number;
  raw: RetentionPurchaseOption;
}

interface BalanceCard {
  type: BalanceType;
  points: number;
  deductRate: number;
}

interface RetentionModalCloseResult {
  refresh?: boolean;
}

@Component({
  selector: 'retention-purchase-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzEmptyModule, NzModalModule, NzSpinModule, I18nPipe, TestIdDirective],
  templateUrl: './retention-purchase-modal.component.html',
  styleUrls: ['./retention-purchase-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RetentionPurchaseModalComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalRef = inject(NzModalRef);
  private readonly modalService = inject(NzModalService);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  readonly modalData: RetentionPurchaseModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  viewStep: 'options' | 'payment' = 'options';
  activeTab: RetentionOverviewTab = 'purchase';
  balanceCards: BalanceCard[] = [];
  deductionInputs: Record<BalanceType, number | null> = {
    uupon: null
  };
  selectedOption: RetentionOptionViewModel | null = null;
  termsAck = false;
  privilegePayload: RetentionPrivilegePayload | null = null;
  submitting = false;
  private aipoolTenantId = '';
  private unpaidOrderTicker: ReturnType<typeof setInterval> | null = null;
  private now = Date.now();
  private preferredActiveTab: RetentionOverviewTab | null = null;
  private shouldRefreshParent = false;

  ngOnInit(): void {
    this.aipoolTenantId = String(this.modalData.aipoolTenantId ?? '').trim();
    this.initializeActiveTab();
    this.startUnpaidOrderTicker();
    this.loadBalanceSummary();
    this.loadPrivilegeCurrent();
  }

  ngOnDestroy(): void {
    this.stopUnpaidOrderTicker();
  }

  t(key: string, params?: Record<string, unknown>): string {
    return this.interpolate(this.i18n.fanyi(key), params);
  }

  private interpolate(template: string, params?: Record<string, unknown>): string {
    return String(template ?? '').replace(/\{(\w+)\}/g, (_, token) => `${params?.[token] ?? `{${token}}`}`);
  }

  get quotaName(): string {
    return String(this.modalData.quotaName ?? '').trim() || this.t('quota.view.retention.modal.default-quota-name');
  }

  get currentValue(): string {
    const activeDays = this.activeRetentionDays;
    if (activeDays !== null) {
      return this.t('quota.view.retention.modal.days-value', { days: activeDays });
    }
    const directValue = String(this.modalData.currentValue ?? '').trim();
    if (directValue) {
      return directValue;
    }
    const currentDays = this.toNullableNumber(this.modalData.currentDays);
    if (currentDays !== null) {
      return this.t('quota.view.retention.modal.days-value', { days: currentDays });
    }
    return '--';
  }

  get currentHint(): string {
    const pieces: string[] = [];
    const defaultDays = this.toNullableNumber(this.modalData.defaultDays);
    const freeDays = this.toNullableNumber(this.modalData.freeDays);
    if (defaultDays !== null) {
      pieces.push(this.t('quota.view.retention.modal.base-quota-days', { days: defaultDays }));
    }
    if (freeDays !== null && freeDays !== defaultDays) {
      pieces.push(this.t('quota.view.retention.modal.free-quota-days', { days: freeDays }));
    }
    return pieces.join('，');
  }

  get currentConfigTitle(): string {
    return this.hasActivePrivilege
      ? this.t('quota.view.retention.modal.current-config-active')
      : this.t('quota.view.retention.modal.current-config');
  }

  get currentPrimaryLabel(): string {
    return this.hasActivePrivilege
      ? this.t('quota.view.retention.modal.current-effective-retention')
      : this.t('quota.view.retention.modal.base-retention');
  }

  get currentStatusMetaLabel(): string {
    if (this.hasPendingPrivilege) {
      return this.t('quota.view.retention.modal.pending-plan');
    }
    if (this.hasActivePrivilege) {
      return this.t('quota.view.retention.modal.enabled-plan');
    }
    return this.t('quota.view.retention.modal.package-status');
  }

  get currentPrimaryHint(): string {
    if (this.hasActivePrivilege) {
      const parts: string[] = [];
      if (this.currentHint) {
        parts.push(this.currentHint);
      }
      if (this.activePrivilege?.expireTime) {
        parts.push(this.t('quota.view.retention.modal.expire-at', { date: this.formatDate(this.activePrivilege.expireTime) }));
      }
      if (this.remainingPrivilegeDays > 0) {
        parts.push(this.t('quota.view.retention.modal.remaining-days', { days: this.remainingPrivilegeDays }));
      }
      return parts.join(' · ');
    }

    return this.currentHint;
  }

  get currentStatusMetaValue(): string {
    if (this.hasPendingPrivilege) {
      return this.formatRetentionDays(this.pendingPrivilege?.retentionDays);
    }
    if (this.hasActivePrivilege) {
      return this.formatRetentionDays(this.activeRetentionDays);
    }
    return this.currentStatusLabel;
  }

  get currentStatusMetaHint(): string {
    if (this.hasPendingPrivilege) {
      const parts = [this.t('quota.view.retention.modal.activate-on', { date: this.queueActivationDateText })];
      if (this.pendingRefundPoints > 0) {
        parts.push(this.t('quota.view.retention.modal.cancel-refund-estimate', { points: this.pendingRefundPoints }));
      }
      return parts.join(' · ');
    }
    if (this.hasActivePrivilege) {
      return this.currentStatusHint;
    }
    return this.currentStatusHint;
  }

  get options(): RetentionOptionViewModel[] {
    return (Array.isArray(this.modalData.options) ? this.modalData.options : [])
      .map((option, index) => this.toOptionViewModel(option, index))
      .sort((left, right) => left.sort - right.sort);
  }

  get selectedOptionKey(): string | null {
    return this.selectedOption ? this.getOptionKey(this.selectedOption.raw, this.selectedOption.sort) : null;
  }

  get hasOptions(): boolean {
    return this.options.length > 0;
  }

  get stepTitle(): string {
    if (this.viewStep === 'options') {
      if (this.activeTab === 'unpaid' && this.hasUnpaidOrder) {
        return `待支付延長包 - ${this.quotaName}`;
      }
      if (this.activeTab === 'queued' && this.hasPendingPrivilege) {
        return `待生效延長包 - ${this.quotaName}`;
      }
      return this.t('quota.view.retention.modal.title-options', { quotaName: this.quotaName });
    }
    return this.t('quota.view.retention.modal.title-payment');
  }

  get stepSubtitle(): string {
    if (this.viewStep === 'options') {
      if (this.activeTab === 'unpaid' && this.hasUnpaidOrder) {
        return this.isUnpaidOrderExpired
          ? '這裡顯示已超時的待支付訂單，刪除後即可重新建立新的延長包訂單。'
          : '這裡顯示目前待支付的延長包訂單，完成支付後才會正式生效。';
      }
      if (this.activeTab === 'queued' && this.hasPendingPrivilege) {
        return '這裡顯示已支付但尚未生效的延長包資訊。';
      }
      return this.t('quota.view.retention.modal.subtitle-options');
    }
    return this.t('quota.view.retention.modal.subtitle-payment');
  }

  get entryFocus(): RetentionEntryFocus {
    const focus = this.modalData.entryFocus;
    return focus === 'unpaid' || focus === 'queued' ? focus : null;
  }

  get isOptionSelected(): boolean {
    return !!this.selectedOption;
  }

  get selectedOptionSummary(): string {
    if (!this.selectedOption) {
      return this.t('quota.view.retention.modal.select-plan-first');
    }
    return `${this.selectedOption.description} - ${this.selectedOption.priceText}`;
  }

  get selectedOptionPrompt(): string {
    if (!this.selectedOption) {
      return this.t('quota.view.retention.modal.prompt-select-plan');
    }
    return this.t('quota.view.retention.modal.prompt-selected-plan', { title: this.selectedOption.title });
  }

  get activePrivilege(): RetentionPrivilegeSnapshot | null {
    return this.privilegePayload?.active ?? null;
  }

  get pendingPrivilege(): RetentionPrivilegeSnapshot | null {
    return this.cancelablePendingPrivilege ?? this.privilegePayload?.pending ?? null;
  }

  get cancelablePendingPrivilege(): RetentionPrivilegeSnapshot | null {
    return this.privilegePayload?.queuedAddon ?? null;
  }

  get unpaidOrder(): RetentionUnpaidOrderSnapshot | null {
    return this.privilegePayload?.unpaidOrder ?? null;
  }

  get hasActivePrivilege(): boolean {
    return !!this.activePrivilege && this.toNullableNumber(this.activePrivilege?.retentionDays) !== null;
  }

  get activeRetentionDays(): number | null {
    return this.toNullableNumber(this.activePrivilege?.retentionDays) ?? this.toNullableNumber(this.modalData.currentDays);
  }

  get selectedRetentionDays(): number | null {
    return this.toNullableNumber(this.selectedOption?.raw?.retentionDays);
  }

  get purchaseScenario(): 'new' | 'renew' | 'upgrade' | 'downgrade' {
    const activeDays = this.activeRetentionDays;
    const selectedDays = this.selectedRetentionDays;
    if (activeDays === null || selectedDays === null) {
      return 'new';
    }
    if (selectedDays === activeDays) {
      return 'renew';
    }
    if (selectedDays > activeDays) {
      return 'upgrade';
    }
    return 'downgrade';
  }

  get hasPendingPrivilege(): boolean {
    return !!this.pendingPrivilege && this.toNullableNumber(this.pendingPrivilege?.retentionDays) !== null;
  }

  get hasUnpaidOrder(): boolean {
    return !!this.unpaidOrder && !this.isUnpaidOrderClosed(this.unpaidOrder);
  }

  get isUnpaidOrderExpired(): boolean {
    const remainingMs = this.unpaidOrderRemainingMs;
    return remainingMs !== null && remainingMs <= 0;
  }

  get scenarioTitle(): string {
    if (this.hasUnpaidOrder) {
      return this.isUnpaidOrderExpired ? '刪除超時訂單' : '繼續支付';
    }
    if (this.hasPendingPrivilege) {
      return this.t('quota.view.retention.modal.pending-exists');
    }
    const titleMap = {
      new: this.t('quota.view.retention.modal.action-buy-now'),
      renew: this.t('quota.view.retention.modal.action-renew-now'),
      upgrade: this.t('quota.view.retention.modal.action-upgrade-now'),
      downgrade: this.t('quota.view.retention.modal.action-confirm-downgrade')
    } as const;
    return titleMap[this.purchaseScenario];
  }

  get scenarioHint(): string {
    if (this.hasUnpaidOrder) {
      return this.isUnpaidOrderExpired ? this.t('quota.view.retention.modal.hint-unpaid-expired') : this.unpaidOrderSummaryText;
    }
    if (this.hasPendingPrivilege) {
      return this.t('quota.view.retention.modal.hint-pending-exists', {
        plan: this.formatRetentionDays(this.pendingPrivilege?.retentionDays)
      });
    }
    if (!this.selectedOption) {
      return this.t('quota.view.retention.modal.prompt-select-plan');
    }
    switch (this.purchaseScenario) {
      case 'renew':
        return this.t('quota.view.retention.modal.hint-renew', { days: this.formatRetentionDays(this.activeRetentionDays) });
      case 'upgrade':
        return this.t('quota.view.retention.modal.hint-upgrade', { days: this.formatRetentionDays(this.selectedRetentionDays) });
      case 'downgrade':
        return this.t('quota.view.retention.modal.hint-downgrade', { days: this.formatRetentionDays(this.selectedRetentionDays) });
      default:
        return this.t('quota.view.retention.modal.hint-new', { days: this.formatRetentionDays(this.selectedRetentionDays) });
    }
  }

  get scenarioHintHtml(): string {
    if (this.hasUnpaidOrder) {
      const tabLabel = this.t('quota.view.retention.modal.tab-unpaid-emphasis');
      if (this.isUnpaidOrderExpired) {
        return this.t('quota.view.retention.modal.hint-unpaid-expired-html', { tabLabel });
      }
      return this.t('quota.view.retention.modal.hint-unpaid-existing-html', { tabLabel });
    }
    if (this.hasPendingPrivilege) {
      return '請先切換到<span class="retention-scenario__emphasis">上方「待生效」分頁</span>，點擊<span class="retention-scenario__emphasis">取消待生效套餐</span>後，再購買新的延長方案。';
    }
    return this.scenarioHint;
  }

  get scenarioTone(): 'neutral' | 'positive' | 'warning' {
    if (this.hasUnpaidOrder || this.hasPendingPrivilege || this.purchaseScenario === 'downgrade') {
      return 'warning';
    }
    if (this.purchaseScenario === 'renew' || this.purchaseScenario === 'upgrade') {
      return 'positive';
    }
    return 'neutral';
  }

  get currentStatusLabel(): string {
    if (this.hasUnpaidOrder) {
      return this.isUnpaidOrderExpired ? '有超時訂單' : '有待支付訂單';
    }
    if (this.hasPendingPrivilege) {
      return this.t('quota.view.retention.modal.status-pending');
    }
    if (this.hasActivePrivilege) {
      return this.t('quota.view.retention.modal.status-purchased');
    }
    return this.t('quota.view.retention.modal.status-not-purchased');
  }

  get currentStatusHint(): string {
    if (this.hasUnpaidOrder) {
      return this.isUnpaidOrderExpired ? this.t('quota.view.retention.modal.status-hint-unpaid-expired') : this.unpaidOrderSummaryText;
    }
    if (this.hasPendingPrivilege) {
      return this.t('quota.view.retention.modal.status-hint-pending');
    }
    if (this.hasActivePrivilege) {
      return this.t('quota.view.retention.modal.status-hint-purchased', {
        days: this.formatRetentionDays(this.activeRetentionDays)
      });
    }
    return this.t('quota.view.retention.modal.status-hint-not-purchased');
  }

  get pendingCancelHint(): string {
    if (this.pendingRefundPoints > 0) {
      return this.t('quota.view.retention.modal.pending-cancel-hint-estimate', { points: this.pendingRefundPoints });
    }
    return this.t('quota.view.retention.modal.pending-cancel-hint');
  }

  get unpaidOrderHint(): string {
    return this.isUnpaidOrderExpired
      ? this.t('quota.view.retention.modal.unpaid-order-expired-hint')
      : this.t('quota.view.retention.modal.unpaid-order-pending-hint');
  }

  get shouldShowUpgradeEstimate(): boolean {
    return !!this.selectedOption && this.purchaseScenario === 'upgrade' && this.selectedOptionUpgradeCreditCash > 0;
  }

  get showTermsConfirmation(): boolean {
    return !!this.selectedOption && !this.hasPendingPrivilege && this.activeTab === 'purchase';
  }

  get shouldShowOptionSelection(): boolean {
    return this.activeTab === 'purchase';
  }

  get availableTabs(): Array<{ key: RetentionOverviewTab; label: string }> {
    const tabs: Array<{ key: RetentionOverviewTab; label: string }> = [{ key: 'purchase', label: '購買 / 續費' }];
    if (this.hasUnpaidOrder) {
      tabs.push({ key: 'unpaid', label: '待支付' });
    }
    if (this.hasPendingPrivilege) {
      tabs.push({ key: 'queued', label: '待生效' });
    }
    return tabs;
  }

  get showPurchaseTab(): boolean {
    return this.activeTab === 'purchase';
  }

  get shouldShowCurrentConfig(): boolean {
    return this.viewStep === 'options' && this.showPurchaseTab;
  }

  get showUnpaidTab(): boolean {
    return this.activeTab === 'unpaid' && this.hasUnpaidOrder;
  }

  get showQueuedTab(): boolean {
    return this.activeTab === 'queued' && this.hasPendingPrivilege;
  }

  get termsSummary(): string {
    if (!this.selectedOption) {
      return '';
    }
    const summaryMap = {
      new: this.t('quota.view.retention.modal.terms-summary-new', {
        days: this.formatRetentionDays(this.selectedRetentionDays)
      }),
      renew: this.t('quota.view.retention.modal.terms-summary-renew', {
        days: this.formatRetentionDays(this.activeRetentionDays)
      }),
      upgrade: this.t('quota.view.retention.modal.terms-summary-upgrade', {
        from: this.formatRetentionDays(this.activeRetentionDays),
        to: this.formatRetentionDays(this.selectedRetentionDays)
      }),
      downgrade: this.t('quota.view.retention.modal.terms-summary-downgrade', {
        from: this.formatRetentionDays(this.activeRetentionDays),
        to: this.formatRetentionDays(this.selectedRetentionDays)
      })
    } as const;
    return summaryMap[this.purchaseScenario];
  }

  get termsItems(): string[] {
    if (!this.selectedOption) {
      return [];
    }
    const items: string[] = [];
    switch (this.purchaseScenario) {
      case 'renew':
        items.push(
          this.t('quota.view.retention.modal.terms-renew-1'),
          this.t('quota.view.retention.modal.terms-renew-2', {
            date: this.currentExpireDateText,
            days: this.formatRetentionDays(this.activeRetentionDays)
          })
        );
        break;
      case 'upgrade':
        items.push(
          this.t('quota.view.retention.modal.terms-upgrade-1', {
            from: this.formatRetentionDays(this.activeRetentionDays),
            to: this.formatRetentionDays(this.selectedRetentionDays)
          }),
          this.t('quota.view.retention.modal.terms-upgrade-2', {
            credit: this.formatCurrency(this.selectedOptionUpgradeCreditCash),
            payable: this.formatCurrency(this.getBasePayableCash())
          })
        );
        break;
      case 'downgrade':
        items.push(
          this.t('quota.view.retention.modal.terms-downgrade-1', {
            to: this.formatRetentionDays(this.selectedRetentionDays),
            date: this.queueActivationDateText
          }),
          this.t('quota.view.retention.modal.terms-downgrade-2', {
            days: this.formatRetentionDays(this.selectedRetentionDays)
          })
        );
        break;
      default:
        items.push(
          this.t('quota.view.retention.modal.terms-new-1', {
            days: this.formatRetentionDays(this.selectedRetentionDays)
          }),
          this.t('quota.view.retention.modal.terms-new-2')
        );
        break;
    }
    items.push(this.t('quota.view.retention.modal.terms-common-pending'), this.t('quota.view.retention.modal.terms-common-refund'));
    return items;
  }

  get remainingPrivilegeDays(): number {
    const expireTime = this.toNullableNumber(this.activePrivilege?.expireTime);
    if (!expireTime) {
      return 0;
    }
    return Math.max(0, Math.ceil((expireTime - Date.now()) / (24 * 60 * 60 * 1000)));
  }

  get pendingRefundPoints(): number {
    const compensationPoints = this.pickFirstNumber([
      this.pendingPrivilege?.compensationAilePoints,
      this.unpaidOrder?.compensationAilePoints
    ]);
    if (compensationPoints !== null) {
      return Math.max(0, Math.floor(compensationPoints));
    }
    const pendingPrice = this.toNullableNumber(this.pendingPrivilege?.priceCny);
    if (pendingPrice === null) {
      return 0;
    }
    return Math.max(0, Math.floor(pendingPrice * 4));
  }

  get queueActivationDateText(): string {
    const timestamp =
      this.toNullableNumber(this.pendingPrivilege?.activateTime) ??
      this.toNullableNumber(this.pendingPrivilege?.startTime) ??
      this.toNullableNumber(this.activePrivilege?.expireTime);
    return timestamp ? this.formatDate(timestamp) : '--';
  }

  get actualPayableDisplay(): string {
    return this.formatCurrency(this.getPayableCash());
  }

  get displayedPayableDisplay(): string {
    return this.actualPayableDisplay;
  }

  get canProceedPurchase(): boolean {
    if (this.hasUnpaidOrder) {
      return false;
    }
    if (!this.selectedOption) {
      return false;
    }
    if (this.hasPendingPrivilege) {
      return false;
    }
    if (!this.termsAck) {
      return false;
    }
    return true;
  }

  get showPendingCancelPlaceholder(): boolean {
    return this.canCancelPendingPrivilege;
  }

  get unpaidOrderSummaryText(): string {
    if (this.isUnpaidOrderExpired) {
      return '延長包訂單已超時，無法繼續支付，可刪除後重新購買。';
    }
    const parts: string[] = [];
    const retentionDays = this.unpaidOrderRetentionDays;
    if (retentionDays !== null) {
      parts.push(`${retentionDays} 天方案待支付`);
    } else {
      parts.push('目前有延長包訂單待支付');
    }
    const expireText = this.resolveSnapshotDateText(this.unpaidOrder?.payExpireTime);
    if (expireText) {
      parts.push(`支付保留至 ${expireText}`);
    }
    return parts.join(' · ');
  }

  get unpaidOrderPriceText(): string {
    const payableCash = this.toNullableNumber(this.unpaidOrder?.payableCash);
    if (payableCash !== null) {
      return this.formatCurrency(payableCash);
    }
    const payable = this.toNullableNumber(this.unpaidOrder?.priceCny);
    if (payable !== null) {
      return this.formatCurrency(payable);
    }
    const originalPrice = this.toNullableNumber(this.unpaidOrder?.originalPriceCny);
    const credit = this.toNullableNumber(this.unpaidOrder?.upgradeCreditCny) ?? 0;
    if (originalPrice !== null) {
      return this.formatCurrency(Math.max(0, originalPrice - credit));
    }
    return '--';
  }

  get unpaidOrderCreateDateText(): string {
    return this.resolveSnapshotDateText(this.unpaidOrder?.createTime ?? this.unpaidOrder?.payStartTime) ?? '--';
  }

  get unpaidOrderExpireDateText(): string {
    return this.resolveSnapshotDateText(this.unpaidOrder?.payExpireTime) ?? '--';
  }

  get unpaidOrderPlanText(): string {
    return this.formatRetentionDays(this.unpaidOrderRetentionDays);
  }

  get unpaidOrderRetentionDays(): number | null {
    return this.toNullableNumber(this.unpaidOrder?.targetRetentionDays) ?? this.toNullableNumber(this.unpaidOrder?.retentionDays);
  }

  get showUnpaidOrderPlan(): boolean {
    return this.unpaidOrderRetentionDays !== null;
  }

  get showUnpaidOrderPrice(): boolean {
    return this.unpaidOrderPriceText !== '--';
  }

  get showUnpaidOrderCreateDate(): boolean {
    return this.unpaidOrderCreateDateText !== '--';
  }

  get unpaidOrderRemainingMs(): number | null {
    return getPaymentRemainingMs(this.unpaidOrder, this.now);
  }

  get unpaidOrderCountdownText(): string {
    const remainingMs = this.unpaidOrderRemainingMs;
    if (remainingMs === null) {
      return '--';
    }
    return this.isUnpaidOrderExpired ? '已超時' : formatCountdown(remainingMs);
  }

  get canContinueUnpaidOrderPayment(): boolean {
    return this.hasUnpaidOrder && !this.isUnpaidOrderExpired && !this.submitting;
  }

  get canDeleteUnpaidOrder(): boolean {
    return this.hasUnpaidOrder && !this.submitting;
  }

  get canCancelPendingPrivilege(): boolean {
    const orderId = String(this.cancelablePendingPrivilege?.orderId ?? '').trim();
    return !!orderId && !this.submitting;
  }

  get pendingCancelUnavailableText(): string {
    return this.t('quota.view.retention.modal.warning-cancel-pending-unavailable');
  }

  get upgradeCreditCashText(): string {
    return `-${this.formatCurrency(this.selectedOptionUpgradeCreditCash)}`;
  }

  get pendingRefundPointsText(): string {
    return this.t('quota.view.retention.modal.aile-points', { points: this.pendingRefundPoints });
  }

  get termsAckText(): string {
    return this.t('quota.view.retention.modal.terms-ack');
  }

  get currentExpireDateText(): string {
    const expireTime = this.toNullableNumber(this.activePrivilege?.expireTime);
    return expireTime ? this.formatDate(expireTime) : this.t('quota.view.retention.modal.after-current-expire');
  }

  selectOption(option: RetentionOptionViewModel): void {
    this.selectedOption = option;
    this.termsAck = false;
    this.cdr.markForCheck();
  }

  isSelectedOption(option: RetentionOptionViewModel): boolean {
    return this.selectedOptionKey === option.key;
  }

  trackOption(_index: number, option: RetentionOptionViewModel): string {
    return option.key;
  }

  trackTab(_index: number, tab: { key: RetentionOverviewTab; label: string }): RetentionOverviewTab {
    return tab.key;
  }

  proceedToPayment(): void {
    if (this.hasUnpaidOrder) {
      if (this.isUnpaidOrderExpired) {
        this.cancelExpiredUnpaidOrder();
      } else {
        this.continueUnpaidOrderPayment();
      }
      return;
    }
    if (!this.selectedOption) {
      this.message.warning(this.t('quota.view.retention.modal.warning-select-plan'));
      return;
    }
    if (this.hasPendingPrivilege) {
      this.message.warning(this.t('quota.view.retention.modal.warning-pending-exists'));
      return;
    }
    if (!this.termsAck) {
      this.message.warning(this.t('quota.view.retention.modal.warning-terms-unconfirmed'));
      return;
    }
    this.viewStep = 'payment';
    this.deductionInputs = { uupon: null };
    this.cdr.markForCheck();
  }

  backToOptions(): void {
    this.viewStep = 'options';
    this.deductionInputs = { uupon: null };
    this.cdr.markForCheck();
  }

  close(): void {
    this.closeWithRefreshFlag();
  }

  continueUnpaidOrderPayment(): void {
    if (this.isUnpaidOrderExpired) {
      this.message.warning(this.t('quota.view.retention.modal.warning-unpaid-order-expired'));
      return;
    }
    const orderId = String(this.unpaidOrder?.orderId ?? '').trim();
    const tenantId = String(this.modalData.tenantId ?? '').trim();
    if (!orderId || !tenantId) {
      this.message.warning(this.t('quota.view.retention.modal.warning-plan-config-missing'));
      return;
    }
    const paymentUrl = String(this.unpaidOrder?.paymentUrl ?? '').trim();
    if (paymentUrl) {
      this.openPaymentModal(paymentUrl, orderId);
      return;
    }
    this.setSubmitting(true);
    this.payOrder(orderId, tenantId);
  }

  cancelUnpaidOrder(): void {
    const orderId = String(this.unpaidOrder?.orderId ?? '').trim();
    if (!orderId) {
      this.message.warning(this.t('quota.view.retention.modal.warning-unpaid-order-missing'));
      return;
    }
    this.cancelRetentionOrder(orderId, '刪除訂單失敗');
  }

  cancelExpiredUnpaidOrder(): void {
    this.cancelUnpaidOrder();
  }

  cancelPendingPrivilege(): void {
    const orderId = String(this.cancelablePendingPrivilege?.orderId ?? '').trim();
    if (!orderId) {
      this.message.warning(this.pendingCancelUnavailableText);
      return;
    }
    this.cancelRetentionOrder(orderId, '取消待生效套餐失敗');
  }

  selectOverviewTab(tab: RetentionOverviewTab): void {
    if (!this.isTabAvailable(tab)) {
      return;
    }
    this.preferredActiveTab = tab;
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  formatPoints(value: number): string {
    return new Intl.NumberFormat(this.i18n.currentLang || undefined).format(Number(value || 0));
  }

  getBalanceTypeLabel(type: BalanceType): string {
    return this.t('quota.view.retention.modal.deduct-uupon');
  }

  getBalancePointsLabel(points: number): string {
    return this.t('quota.view.retention.modal.balance-points', { points: this.formatPoints(points) });
  }

  getSelectedOptionPrice(): number {
    return Number(this.selectedOption?.price ?? 0) || 0;
  }

  get selectedOptionUpgradeCreditCash(): number {
    if (!this.selectedOption || this.purchaseScenario !== 'upgrade') {
      return 0;
    }
    return this.getOptionUpgradeCreditCash(this.selectedOption);
  }

  get selectedOptionEstimatedPayableCash(): number | null {
    if (!this.selectedOption || this.purchaseScenario !== 'upgrade') {
      return null;
    }
    return this.getOptionEstimatedPayableCash(this.selectedOption);
  }

  get selectedOptionEstimatedRefundPoints(): number {
    if (!this.selectedOption || this.purchaseScenario !== 'upgrade') {
      return 0;
    }
    return this.resolveOptionEstimatedRefundPoints(this.selectedOption);
  }

  shouldShowOptionUpgradePreview(option: RetentionOptionViewModel): boolean {
    if (this.hasPendingPrivilege || !this.hasActivePrivilege) {
      return false;
    }
    if (!this.isUpgradeOption(option)) {
      return false;
    }
    return this.getOptionUpgradeCreditCash(option) > 0 || this.getOptionEstimatedPayableCash(option) !== null;
  }

  getOptionUpgradeCreditCashText(option: RetentionOptionViewModel): string {
    return `-${this.formatCurrency(this.getOptionUpgradeCreditCash(option))}`;
  }

  getOptionUpgradePayableText(option: RetentionOptionViewModel): string {
    const payable = this.getOptionEstimatedPayableCash(option);
    return this.formatCurrency(payable ?? option.price);
  }

  getOptionEstimatedRefundPoints(option: RetentionOptionViewModel): number {
    return this.resolveOptionEstimatedRefundPoints(option);
  }

  getOptionEstimatedRefundPointsText(option: RetentionOptionViewModel): string {
    return this.t('quota.view.retention.modal.aile-points', { points: this.getOptionEstimatedRefundPoints(option) });
  }

  get selectedOptionEstimatedRefundPointsText(): string {
    return this.t('quota.view.retention.modal.aile-points', { points: this.selectedOptionEstimatedRefundPoints });
  }

  getOptionPriceHint(option: RetentionOptionViewModel): string {
    return String(option.raw?.priceHint ?? '').trim();
  }

  getDeductPoints(type: BalanceType): number {
    return this.toNonNegativeInteger(this.deductionInputs[type]);
  }

  getDeductedCash(type: BalanceType): number {
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return 0;
    const points = this.getDeductPoints(type);
    return points > 0 ? points / card.deductRate : 0;
  }

  getPayableCash(): number {
    const payable = this.getBasePayableCash() - this.getDeductedCash('uupon');
    return Math.max(0, Number(payable.toFixed(2)));
  }

  getPayableDisplay(): string {
    return this.formatCurrency(this.getPayableCash());
  }

  getDeductDisplay(type: BalanceType): string {
    return this.formatCurrency(this.getDeductedCash(type));
  }

  getDeductRateText(rate: number): string {
    return this.t('quota.view.retention.modal.deduct-rate', { rate });
  }

  getDeductAmountText(type: BalanceType): string {
    return this.t('quota.view.retention.modal.deduct-amount', { amount: this.getDeductDisplay(type) });
  }

  blockNonIntegerInput(event: InputEvent): void {
    if (event.data && !/^\d+$/.test(event.data)) {
      event.preventDefault();
    }
  }

  onDeductInput(type: BalanceType, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const integerText = input.value.replace(/\D+/g, '');
    this.applyDeductInput(type, integerText);
    input.value = this.deductionInputs[type] === null ? '' : String(this.deductionInputs[type]);
  }

  applyDeductInput(type: BalanceType, value: string | number | null | undefined): void {
    if (this.isDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;

    const raw = Number(value ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) {
      this.deductionInputs[type] = null;
      return;
    }

    const optionPrice = this.getBasePayableCash();
    if (raw / card.deductRate >= optionPrice) {
      const maxPointsByOption = Math.floor(optionPrice * card.deductRate);
      const points = Math.min(card.points, maxPointsByOption);
      this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
      this.syncDeductionLock(type);
      return;
    }

    const maxByBalance = card.points;
    const maxByPrice = Math.floor(this.getRemainingCashFor(type) * card.deductRate);
    const normalized = Math.floor(Math.min(raw, maxByBalance, maxByPrice));
    this.deductionInputs[type] = normalized > 0 ? normalized : null;
    this.syncDeductionLock(type);
    this.cdr.markForCheck();
  }

  deductAll(type: BalanceType): void {
    if (this.isDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;
    const optionPrice = this.getBasePayableCash();
    const maxPointsByOption = Math.floor(optionPrice * card.deductRate);

    if (card.points >= maxPointsByOption) {
      this.deductionInputs[type] = this.normalizeUUPonMultiple(maxPointsByOption);
      this.syncDeductionLock(type);
      return;
    }

    const maxPointsByPrice = Math.floor(this.getRemainingCashFor(type) * card.deductRate);
    const points = Math.min(card.points, maxPointsByPrice);
    this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
    this.syncDeductionLock(type);
    this.cdr.markForCheck();
  }

  isDeductInputDisabled(type: BalanceType): boolean {
    return false;
  }

  confirmPay(): void {
    if (this.submitting) {
      return;
    }
    if (!this.validateUUPonMultiple()) {
      return;
    }
    if (!this.selectedOption) {
      this.message.warning(this.t('quota.view.retention.modal.warning-select-plan'));
      return;
    }
    const tenantId = String(this.modalData.tenantId ?? '').trim();
    if (!tenantId) {
      this.message.warning(this.t('quota.view.retention.modal.warning-tenant-missing'));
      return;
    }

    const selectedRuleId = String(this.selectedOption.raw?.ruleId ?? '').trim();
    const quotaCode = String(this.modalData.quotaCode ?? '').trim();
    const aipoolTenantId = String(this.aipoolTenantId || this.modalData.aipoolTenantId || '').trim();
    if (!quotaCode || !selectedRuleId || !aipoolTenantId) {
      this.message.warning(this.t('quota.view.retention.modal.warning-plan-config-missing'));
      return;
    }
    this.aipoolTenantId = aipoolTenantId;
    this.setSubmitting(true);

    const payload: any = {
      quotaCode,
      ruleId: selectedRuleId,
      aipoolTenantId,
      deductUUPonPoints: this.getDeductPoints('uupon'),
      riskAck: this.termsAck
    };

    this.http.post(ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_CREATE, payload).subscribe({
      next: res => {
        if (!res?.success) {
          this.setSubmitting(false);
          return;
        }
        this.markParentForRefresh();
        const raw = res?.data ?? res;
        const orderId = this.resolveOrderId(raw);
        if (!orderId) {
          this.setSubmitting(false);
          return;
        }
        if (this.isPayRequired(raw)) {
          this.payOrder(orderId, tenantId);
          return;
        }
        this.setSubmitting(false);
        this.openPaymentUrl(raw, orderId);
        this.modalRef.close({ refresh: true });
      },
      error: () => {
        this.setSubmitting(false);
      }
    });
  }

  private loadBalanceSummary(): void {
    this.balanceCards = this.buildBalanceCards();
    this.http.post(ConstantUtil.ADMIN_ROBOT_ORDER_V1_POINTS, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) return;
        this.aipoolTenantId = String(this.aipoolTenantId || res?.data?.aipoolTenantId || '').trim();
        this.balanceCards = this.buildBalanceCards(res?.data);
        this.deductionInputs = { uupon: null };
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  private loadPrivilegeCurrent(): void {
    const tenantId = String(this.modalData.tenantId ?? '').trim();
    const quotaCode = String(this.modalData.quotaCode ?? '').trim();
    if (!tenantId || !quotaCode) {
      return;
    }

    this.http
      .post(ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_PRIVILEGE_V1_CURRENT, {
        tenantId,
        quotaCode
      })
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            return;
          }
          this.privilegePayload = (res?.data ?? res) as RetentionPrivilegePayload;
          this.loadUnpaidOrderDetail();
          this.normalizeActiveTab();
          this.cdr.markForCheck();
        },
        error: () => {}
      });
  }

  private loadUnpaidOrderDetail(): void {
    const orderId = String(this.privilegePayload?.unpaidOrder?.orderId ?? '').trim();
    const tenantId = String(this.modalData.tenantId ?? '').trim();
    if (!orderId || !tenantId) {
      return;
    }

    const apiPath = ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_ITEM.replace('{orderId}', encodeURIComponent(orderId));
    const payload: Record<string, string> = {
      orderId,
      tenantId
    };
    const aipoolTenantId = String(this.aipoolTenantId || this.modalData.aipoolTenantId || '').trim();
    if (aipoolTenantId) {
      payload['aipoolTenantId'] = aipoolTenantId;
    }

    this.http.post(apiPath, payload).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess || !this.privilegePayload?.unpaidOrder) {
          return;
        }
        const detail = (res?.data ?? res) as RetentionUnpaidOrderSnapshot;
        this.privilegePayload = {
          ...this.privilegePayload,
          unpaidOrder: {
            ...this.privilegePayload.unpaidOrder,
            ...detail
          }
        };
        this.normalizeActiveTab();
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  private buildBalanceCards(raw?: any): BalanceCard[] {
    return [{ type: 'uupon', points: this.toNonNegativeInteger(raw?.uuponPoint), deductRate: 4 }];
  }

  private getBasePayableCash(): number {
    const estimatedPayableCash = this.selectedOptionEstimatedPayableCash;
    if (estimatedPayableCash !== null) {
      return Math.max(0, Number(estimatedPayableCash.toFixed(2)));
    }
    return this.getSelectedOptionPrice();
  }

  private isUpgradeOption(option: RetentionOptionViewModel): boolean {
    const actionType = String(option.raw?.actionType ?? '')
      .trim()
      .toLowerCase();
    if (actionType) {
      return actionType === 'upgrade';
    }
    const optionDays = this.toNullableNumber(option.raw?.retentionDays);
    const activeDays = this.activeRetentionDays;
    if (optionDays === null || activeDays === null) {
      return false;
    }
    return optionDays > activeDays;
  }

  private cancelRetentionOrder(orderId: string, fallbackMessage: string): void {
    const apiPath = ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_CANCEL.replace('{orderId}', encodeURIComponent(orderId));
    this.setSubmitting(true);
    this.http.post(apiPath, {}, undefined, { silentAllErrors: true }).subscribe({
      next: res => {
        this.setSubmitting(false);
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          this.message.warning(res?.msg || fallbackMessage);
          return;
        }
        this.markParentForRefresh();
        this.preferredActiveTab = 'purchase';
        this.activeTab = 'purchase';
        this.loadPrivilegeCurrent();
      },
      error: err => {
        this.setSubmitting(false);
        this.message.warning(err?.error?.msg || fallbackMessage);
      }
    });
  }

  private getOptionUpgradeCreditCash(option: RetentionOptionViewModel): number {
    const credit = this.pickFirstNumber([option.raw?.estimatedUpgradeCreditCny, option.raw?.upgradeCreditCny]);
    return credit !== null ? Math.max(0, Number(credit.toFixed(2))) : 0;
  }

  private getOptionEstimatedPayableCash(option: RetentionOptionViewModel): number | null {
    const payable = this.pickFirstNumber([option.raw?.estimatedPayableCash, option.raw?.payableCash]);
    return payable !== null ? Math.max(0, Number(payable.toFixed(2))) : null;
  }

  private resolveOptionEstimatedRefundPoints(option: RetentionOptionViewModel): number {
    const refundPoints = this.toNullableNumber(option.raw?.estimatedRefundAilePoints);
    return refundPoints !== null ? Math.max(0, Math.floor(refundPoints)) : 0;
  }

  private toOptionViewModel(option: RetentionPurchaseOption, index: number): RetentionOptionViewModel {
    const effectValue = String(option?.effectValue ?? '').trim();
    const effectUnit = String(option?.effectUnit ?? '').trim();
    const title =
      String(option?.name ?? '').trim() ||
      (effectValue ? `${effectValue}${effectUnit ? ` ${effectUnit}` : ''}` : this.t('quota.view.retention.modal.default-plan-name'));
    const description = this.buildOptionDescription(option, effectValue, effectUnit);
    const extraDays = this.toNullableNumber(option?.extraDays);
    const price = this.toNullableNumber(option?.price);
    const unitPrice = String(option?.unitPrice ?? '').trim();
    const currency = String(option?.currency ?? '').trim() || this.t('quota.view.retention.modal.currency-unit');

    return {
      key: this.getOptionKey(option, index),
      title,
      description,
      priceText:
        price !== null
          ? this.t('quota.view.retention.modal.price-per-year', { price, currency })
          : unitPrice
            ? this.t('quota.view.retention.modal.price-unit-per-year', { value: unitPrice })
            : '--',
      extraText: extraDays !== null ? this.t('quota.view.retention.modal.extra-days', { days: extraDays }) : '',
      recommend: !!option?.recommend,
      sort: this.toNullableNumber(option?.sort) ?? index,
      price: price ?? 0,
      raw: option
    };
  }

  private getOptionKey(option: RetentionPurchaseOption | null | undefined, fallbackIndex: number): string {
    const ruleId = String(option?.ruleId ?? '').trim();
    if (ruleId) {
      return ruleId;
    }

    const retentionDays = this.toNullableNumber(option?.retentionDays);
    const actionType = String(option?.actionType ?? '').trim();
    const name = String(option?.name ?? '').trim();
    return [name, retentionDays ?? '', actionType, fallbackIndex].join('::');
  }

  private buildOptionDescription(option: RetentionPurchaseOption, effectValue: string, effectUnit: string): string {
    if (effectValue) {
      const normalizedUnit = effectUnit.toUpperCase() === 'DAY' ? this.t('quota.view.retention.modal.unit-day') : effectUnit;
      if (effectUnit.toUpperCase() === 'DAY') {
        return this.t('quota.view.retention.modal.retention-capability', { days: effectValue });
      }
      return normalizedUnit ? `${effectValue} ${normalizedUnit}` : effectValue;
    }

    const retentionDays = this.toNullableNumber(option?.retentionDays);
    if (retentionDays !== null) {
      return this.t('quota.view.retention.modal.retention-capability', { days: retentionDays });
    }

    return String(option?.description ?? '').trim();
  }

  private toNullableNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private pickFirstNumber(values: unknown[]): number | null {
    for (const value of values) {
      const parsed = this.toNullableNumber(value);
      if (parsed !== null) {
        return parsed;
      }
    }
    return null;
  }

  private toNonNegativeInteger(value: unknown): number {
    const parsed = Number(value ?? 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.floor(parsed);
  }

  private getRemainingCashFor(type: BalanceType): number {
    const optionPrice = this.getBasePayableCash();
    return Math.max(0, optionPrice);
  }

  formatRetentionDays(value: unknown): string {
    const days = this.toNullableNumber(value);
    return days !== null ? this.t('quota.view.retention.modal.days-value', { days }) : '--';
  }

  formatCurrency(value: unknown): string {
    const amount = this.toNullableNumber(value);
    return amount !== null ? this.t('quota.view.retention.modal.currency-yuan', { value: amount.toFixed(0) }) : '--';
  }

  formatDate(value: unknown): string {
    const time = this.toNullableNumber(value);
    if (!time) {
      return '--';
    }
    return new Intl.DateTimeFormat(this.i18n.currentLang || undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(time);
  }

  private resolveSnapshotDateText(value: unknown): string | null {
    const time = this.toNullableNumber(value);
    return time ? this.formatDate(time) : null;
  }

  private initializeActiveTab(): void {
    if (this.entryFocus === 'unpaid') {
      this.preferredActiveTab = 'unpaid';
      this.activeTab = 'unpaid';
      return;
    }
    if (this.entryFocus === 'queued') {
      this.preferredActiveTab = 'queued';
      this.activeTab = 'queued';
      return;
    }
    this.preferredActiveTab = 'purchase';
    this.activeTab = 'purchase';
  }

  private normalizeActiveTab(): void {
    if (this.preferredActiveTab && this.isTabAvailable(this.preferredActiveTab)) {
      this.activeTab = this.preferredActiveTab;
      return;
    }
    this.activeTab = this.getFallbackTab(this.activeTab);
    this.preferredActiveTab = this.activeTab;
  }

  private isTabAvailable(tab: RetentionOverviewTab): boolean {
    if (tab === 'unpaid') {
      return this.hasUnpaidOrder;
    }
    if (tab === 'queued') {
      return this.hasPendingPrivilege;
    }
    return true;
  }

  private getFallbackTab(tab: RetentionOverviewTab): RetentionOverviewTab {
    if (this.isTabAvailable(tab)) {
      return tab;
    }
    if (tab === 'unpaid' && this.hasPendingPrivilege) {
      return 'queued';
    }
    if (tab === 'queued' && this.hasUnpaidOrder) {
      return 'unpaid';
    }
    return 'purchase';
  }

  private startUnpaidOrderTicker(): void {
    if (this.unpaidOrderTicker) {
      return;
    }
    this.unpaidOrderTicker = setInterval(() => {
      this.now = Date.now();
      if (this.hasUnpaidOrder) {
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  private stopUnpaidOrderTicker(): void {
    if (!this.unpaidOrderTicker) {
      return;
    }
    clearInterval(this.unpaidOrderTicker);
    this.unpaidOrderTicker = null;
  }

  private syncDeductionLock(type: BalanceType): void {
    return;
  }

  private normalizeUUPonMultiple(points: number): number | null {
    const normalized = Math.floor(Number(points) || 0);
    const multiple = normalized - (normalized % 4);
    return multiple > 0 ? multiple : null;
  }

  private validateUUPonMultiple(): boolean {
    const points = this.getDeductPoints('uupon');
    if (points > 0 && points % 4 !== 0) {
      this.message.warning(this.t('quota.view.retention.modal.uupon-multiple-of-four'));
      return false;
    }
    return true;
  }

  private payOrder(orderId: string, tenantId: string): void {
    const apiPath = ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_PAY.replace('{orderId}', encodeURIComponent(orderId));
    this.http.post(apiPath, this.buildPayPayload(tenantId, orderId)).subscribe({
      next: res => {
        if (!res?.success) {
          this.setSubmitting(false);
          return;
        }
        const payload = res?.data ?? res;
        this.storePaymentSnapshot('retention', orderId, payload);
        this.setSubmitting(false);
        this.openPaymentUrl(payload, orderId);
      },
      error: () => {
        this.setSubmitting(false);
      }
    });
  }

  private setSubmitting(value: boolean): void {
    this.submitting = value;
    this.cdr.markForCheck();
  }

  private isUnpaidOrderClosed(order: RetentionUnpaidOrderSnapshot | null | undefined): boolean {
    if (!order) {
      return true;
    }
    const statusValues = [order.status, order.latestOrderStatus, order.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    return statusValues.some(status =>
      ['paid', 'success', 'succeeded', 'completed', 'complete', 'done', 'failed', 'canceled', 'cancelled', 'closed', 'replaced'].includes(
        status
      )
    );
  }

  private buildPayPayload(tenantId: string, orderId?: string): any {
    const urls = this.buildPaymentReturnUrls('retention', tenantId, orderId);
    const payload: any = {
      tenantId,
      resultURL: urls.resultURL,
      cancelURL: urls.cancelURL
    };
    if (this.aipoolTenantId) {
      payload.aipoolTenantId = this.aipoolTenantId;
    }
    return payload;
  }

  private buildPaymentReturnUrls(
    orderType: PaymentOrderType,
    tenantId: string,
    orderId?: string
  ): { resultURL: string; cancelURL: string } {
    return {
      resultURL: this.buildPaymentReturnUrl(orderType, 'success', tenantId, orderId),
      cancelURL: this.buildPaymentReturnUrl(orderType, 'failed', tenantId, orderId)
    };
  }

  private buildPaymentReturnUrl(
    orderType: PaymentOrderType,
    paymentResult: 'success' | 'failed',
    tenantId: string,
    orderId?: string
  ): string {
    return buildPaymentResultUrl({
      orderType,
      paymentResult,
      tenantId,
      aipoolTenantId: this.aipoolTenantId,
      orderId,
      sourcePath: this.getSourcePath()
    });
  }

  private openPaymentUrl(raw?: any, orderId?: string): void {
    const payUrl = raw?.paymentUrl ?? null;
    if (payUrl) {
      this.openPaymentModal(String(payUrl), orderId);
      return;
    }
    if (this.isPaymentPaid(raw)) {
      this.markParentForRefresh();
      this.closeWithRefreshFlag();
    }
  }

  private openPaymentModal(paymentUrl: string, orderId?: string): void {
    const paymentModal = this.modalService.create({
      nzContent: PaymentIframeModalComponent,
      nzWidth: '1200px',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'payment-iframe-modal',
      nzData: {
        paymentUrl,
        orderId,
        orderType: 'retention'
      }
    });

    paymentModal.afterClose.subscribe((result?: RetentionModalCloseResult) => {
      if (result?.refresh) {
        this.markParentForRefresh();
      }
      this.closeWithRefreshFlag();
    });
  }

  private markParentForRefresh(): void {
    this.shouldRefreshParent = true;
  }

  private closeWithRefreshFlag(): void {
    this.modalRef.close(this.shouldRefreshParent ? { refresh: true } : undefined);
  }

  private getSourcePath(): string {
    return `${window.location.pathname}${window.location.search}`;
  }

  private storePaymentSnapshot(orderType: PaymentOrderType, orderId: string, raw?: any): void {
    storePaymentSessionSnapshot({
      orderType,
      orderId,
      tenantId: String(this.modalData.tenantId ?? '').trim() || undefined,
      aipoolTenantId: String(this.aipoolTenantId ?? raw?.aipoolTenantId ?? '').trim() || undefined,
      paymentUrl: String(raw?.paymentUrl ?? '').trim() || undefined,
      reserveTimeoutSeconds: raw?.reserveTimeoutSeconds,
      payStartTime: raw?.payStartTime,
      payExpireTime: raw?.payExpireTime,
      orderStatus: raw?.orderStatus,
      paymentStatus: raw?.paymentStatus,
      payRequired: raw?.payRequired
    });
  }

  private resolveOrderId(raw?: any): string {
    return String(raw?.orderId ?? '').trim();
  }

  private isPayRequired(raw?: any): boolean {
    return raw?.payRequired === true;
  }

  private isPaymentPaid(raw?: any): boolean {
    const statusValues = [raw?.status, raw?.latestOrderStatus, raw?.orderStatus, raw?.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    if (statusValues.some(status => ['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(status))) {
      return true;
    }
    if (Number(raw?.status) === 30 || Number(raw?.latestOrderStatus ?? raw?.orderStatus) === 30 || Number(raw?.paymentStatus) === 20) {
      return true;
    }
    return raw?.needPay === false || raw?.payRequired === false;
  }
}
